import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, OnInit, OnChanges, SimpleChanges, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentProfitService } from '../services/agent-profit.service';
import { StoreService } from '../services/store.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PagePermission } from '../services/access.service';
import { Subscription } from 'rxjs';

interface ChatMessage {
  sender: 'user' | 'agent';
  text: string;
  html?: SafeHtml;
  rawHtml?: string;
  copied?: boolean;
  timestamp?: number;
}

export interface AgentOption {
  agentId: string;
  name: string;
  icon: string;
  badge: string;
  description: string;
  starterPrompts: string[];
}

export interface DeviceOption {
  deviceId: string;
  deviceName?: string;
  storeId?: string;
  displayLabel: string;
}

export function resolveSmartAgentIcon(agentId?: string, name?: string, explicitIcon?: string): string {
  if (explicitIcon && explicitIcon.trim() && explicitIcon !== '🤖') {
    return explicitIcon.trim();
  }

  const id = (agentId || '').toLowerCase().trim();
  const title = (name || '').toLowerCase().trim();

  // 1. Specific agent ID match
  if (id === 'platform-support' || id.includes('platform')) return '🏢';
  if (id === 'financial-operations' || id.includes('financial') || id.includes('invoice') || id.includes('profit') || id.includes('accounting')) return '💰';
  if (id === 'kiosk-support' || id.includes('kiosk') || id.includes('hardware') || id.includes('terminal') || id.includes('camera') || id.includes('photolavie')) return '📸';
  if (id === 'sales-report' || id.includes('sales')) return '📈';
  if (id === 'daily-report' || id.includes('daily')) return '📅';
  if (id === 'frame-report' || id.includes('frame') || id.includes('artist')) return '🎨';
  if (id === 'user-management' || id.includes('user') || id.includes('access')) return '👥';
  if (id === 'store-management' || id.includes('store')) return '🏬';
  if (id === 'company-management' || id.includes('company')) return '🏛️';

  // 2. Name keywords match
  if (title.includes('platform') || title.includes('erp') || title.includes('operations')) return '🏢';
  if (title.includes('financ') || title.includes('invoice') || title.includes('profit') || title.includes('account') || title.includes('money')) return '💰';
  if (title.includes('kiosk') || title.includes('hardware') || title.includes('terminal') || title.includes('camera') || title.includes('photolavie') || title.includes('booth')) return '📸';
  if (title.includes('sales') || title.includes('revenue')) return '📈';
  if (title.includes('daily')) return '📅';
  if (title.includes('frame') || title.includes('art')) return '🎨';
  if (title.includes('user') || title.includes('access') || title.includes('rbac')) return '👥';
  if (title.includes('store')) return '🏬';
  if (title.includes('company')) return '🏛️';

  return explicitIcon || '🤖';
}

export function resolveSmartAgentBadge(agentId?: string, name?: string, explicitBadge?: string): string {
  if (explicitBadge && explicitBadge.trim() && explicitBadge !== 'AI Specialist') {
    return explicitBadge.trim();
  }

  const id = (agentId || '').toLowerCase().trim();
  const title = (name || '').toLowerCase().trim();

  if (id === 'platform-support' || title.includes('platform')) return 'ERP & Operations';
  if (id === 'financial-operations' || title.includes('financ') || title.includes('invoice')) return 'Invoices & Accounting';
  if (id === 'kiosk-support' || title.includes('kiosk') || title.includes('hardware')) return 'Hardware & Terminal';
  if (id === 'general-support' || title.includes('general') || title.includes('other')) return 'General Assistant';

  return explicitBadge || 'AI Specialist';
}

export const DEFAULT_AGENT_OPTIONS: AgentOption[] = [
  {
    agentId: 'platform-support',
    name: 'Platform Support',
    icon: '🏢',
    badge: 'ERP & Operations',
    description: 'Answers all Web ERP platform questions, navigation, company & store management, Free-Form Canvas layout editor, and operational rules.',
    starterPrompts: [
      'How do I configure Shop-In-Shop (SIS) for a company?',
      'Explain the Free-Form Canvas Theme Layout Editor',
      'How does the Fee Collection entity work for brands?',
      'Show me how to assign stores to a company'
    ]
  },
  {
    agentId: 'financial-operations',
    name: 'Financial Information',
    icon: '💰',
    badge: 'Invoices & Accounting',
    description: 'Deconstructs SMRS invoices, analyzes unpaid company balances, store fee calculations, balance carryovers, and revenue projections.',
    starterPrompts: [
      'Which companies have unpaid invoices for 08/2026?',
      'Deconstruct the SMRS invoice for Able Solutions',
      'Show multi-month revenue trends across stores',
      'Explain how Beginning Balance carryover works'
    ]
  },
  {
    agentId: 'kiosk-support',
    name: 'Kiosk App Support',
    icon: '📸',
    badge: 'Hardware & Terminal',
    description: 'Assistance with PhotoLavie WPF photobooth terminals, hardware diagnostics, incident log analysis, peripheral status, and troubleshooting guidance.',
    starterPrompts: [
      'Check live hardware status and recent errors for PB-STORE01-01',
      'PB-STORE01-01 lost its camera connection, inspect the failure logs',
      'Fetch recent Serilog application logs for PB-STORE01-01',
      'Request terminal to upload its latest application log for PB-STORE01-01'
    ]
  },
  {
    agentId: 'general-support',
    name: 'Other (General)',
    icon: '🤖',
    badge: 'General Assistant',
    description: 'General operational intelligence, refund-request analysis, ad-hoc calculations, cross-module lookups, and open-ended platform inquiries.',
    starterPrompts: [
      'Summarize this month\'s refund requests: volume, common reasons, and most affected machines',
      'What is the most common issue customers reported this month?',
      'Analyze refund ticket RF-834-291-5',
      'Show refund requests for PB-STORE01-01 this month'
    ]
  }
];

@Component({
  selector: 'app-agent-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.scss']
})
export class AgentChatComponent implements OnInit, OnChanges, OnDestroy {
  // Optional context passed when active on a specific report
  @Input() currentPeriod: string = '';
  @Input() dashboardStats: any;
  @Input() gridData: any;
  @Input() systemInstruction?: string;
  @Input() agentName?: string;
  @Input() historicalData?: any[];
  @Input() chartOfAccounts?: string[];
  @Input() userRole?: string;
  @Input() permissions?: PagePermission[] | null;
  @Output() agentAction = new EventEmitter<any>();

  @ViewChild('chatInput') chatInput!: ElementRef<HTMLInputElement>;
  @ViewChild('chatWindow') chatWindow!: ElementRef<HTMLDivElement>;

  hasAccess = false;
  isOpen = false;
  isLoading = false;
  userInput = '';
  chatWidth: number | undefined;
  chatHeight: number | undefined;
  messages: ChatMessage[] = [];

  private rawAgentOptions: AgentOption[] = DEFAULT_AGENT_OPTIONS;
  agentOptions: AgentOption[] = [];
  selectedAgent: AgentOption = DEFAULT_AGENT_OPTIONS[1]; // Default to Financial Information
  activeAgentId: string = 'financial-operations';
  showAgentPicker = false;

  availableDevices: DeviceOption[] = [];
  selectedDeviceId: string = '';
  deviceSearchText: string = '';
  showDeviceDropdownMenu: boolean = false;
  showStarterDeviceDropdownMenu: boolean = false;
  starterDeviceSearchText: string = '';

  availableAgents: { agentId: string; name: string; description?: string; icon?: string; badge?: string; starterPrompts?: string[]; metaprompt: string }[] = [];
  private chatOpenSub?: Subscription;

  constructor(
    private agentProfitService: AgentProfitService, 
    private storeService: StoreService,
    private elementRef: ElementRef, 
    private sanitizer: DomSanitizer
  ) {}

  async ngOnInit() {
    // 1. Initial permission check
    this.applyAgentPermissions();

    // 2. Fetch configured agents from Firestore 'agents' collection
    await this.loadConfiguredAgents();

    // 3. Fetch available photobooth devices for optional targeting
    await this.loadDevices();

    // 4. Load stored user preferences (agentId & deviceId) from users/{uid}/preferences/agent_chat
    const savedPrefs = await this.agentProfitService.loadUserPreferences();
    if (savedPrefs) {
      if (savedPrefs.agentId) {
        const found = this.agentOptions.find(a => a.agentId === savedPrefs.agentId);
        if (found) {
          this.selectedAgent = found;
          this.activeAgentId = found.agentId;
        }
      }
      if (savedPrefs.deviceId) {
        this.selectedDeviceId = savedPrefs.deviceId;
      }
    }

    // 5. Sync external programmatic chat open requests
    this.chatOpenSub = this.agentProfitService.chatOpen$.subscribe(req => {
      if (!this.hasAccess) return;
      if (req.open) {
        this.isOpen = true;
        
        // If an explicit agent was requested (e.g. Chat about this Device -> 'kiosk-support')
        if (req.agentId) {
          const reqId = req.agentId.toLowerCase().trim();
          const targetAgent = this.agentOptions.find(a => 
            a.agentId.toLowerCase() === reqId || 
            a.name.toLowerCase().includes(reqId) || 
            (reqId.includes('kiosk') && a.agentId.includes('kiosk'))
          );
          if (targetAgent) {
            this.selectedAgent = targetAgent;
            this.activeAgentId = targetAgent.agentId;
          }
        }
        if (req.deviceId !== undefined) {
          this.selectedDeviceId = req.deviceId;
        }

        if (req.agentId || req.deviceId !== undefined) {
          this.saveCurrentPreferences();
          // Always refresh greeting when explicitly launched with a target device/agent
          this.setInitialGreeting();
        }

        this.scrollToBottom();
        setTimeout(() => this.chatInput?.nativeElement?.focus(), 100);
      } else if (!req.open && this.isOpen) {
        this.isOpen = false;
      }
    });

    // 6. Load stored chat history
    const history = await this.agentProfitService.loadChatHistory();
    const today = new Date().setHours(0, 0, 0, 0);

    let todaysHistory = [];
    if (history && history.length > 0) {
      todaysHistory = history.filter((msg: any) => msg.timestamp && new Date(msg.timestamp).setHours(0, 0, 0, 0) === today);
    }

    if (todaysHistory.length > 0) {
      this.messages = todaysHistory.map((msg: any) => ({
        ...msg,
        html: this.sanitizer.bypassSecurityTrustHtml(msg.rawHtml || msg.text)
      }));
      this.showAgentPicker = false;
      this.scrollToBottom();

      if (todaysHistory.length !== history.length) {
        this.agentProfitService.saveChatHistory(this.messages);
      }
    } else {
      // If fresh session, display greeting and inline agent selection buttons
      this.showAgentPicker = false;
      this.setInitialGreeting();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.device-filter-dropdown-container')) {
      this.showDeviceDropdownMenu = false;
      this.showStarterDeviceDropdownMenu = false;
    }
  }

  ngOnDestroy() {
    if (this.chatOpenSub) {
      this.chatOpenSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userRole'] || changes['permissions']) {
      this.applyAgentPermissions();
    }
  }

  isAgentAllowed(agentId: string): boolean {
    const role = (this.userRole || '').toLowerCase().trim();
    if (!role) return false;
    if (role === 'superadmin') return true;

    // 1. Parent 'chat-agent' must be granted
    if (this.permissions && this.permissions.length > 0) {
      const parentPerm = this.permissions.find(p => p.id === 'chat-agent');
      if (parentPerm && parentPerm.roles) {
        const isParentAllowed = parentPerm.roles.some(r => r.toLowerCase() === role);
        if (!isParentAllowed) {
          return false;
        }
      }
    }

    // 2. Specific sub-agent permission check
    const permKey = `chat-agent_${agentId}`;
    if (this.permissions && this.permissions.length > 0) {
      const perm = this.permissions.find(p => p.id === permKey);
      if (perm && perm.roles) {
        return perm.roles.some(r => r.toLowerCase() === role);
      }
      // If specific sub-permission document doesn't exist yet, fallback to parent page permission
      const parentPerm = this.permissions.find(p => p.id === 'chat-agent');
      if (parentPerm && parentPerm.roles) {
        return parentPerm.roles.some(r => r.toLowerCase() === role);
      }
    }

    return false;
  }

  private applyAgentPermissions() {
    const role = (this.userRole || '').toLowerCase().trim();
    const isSuperAdmin = role === 'superadmin';

    // 1. Check parent chat-agent permission
    let isParentAllowed = isSuperAdmin;
    if (!isSuperAdmin && this.permissions && this.permissions.length > 0) {
      const parentPerm = this.permissions.find(p => p.id === 'chat-agent');
      if (parentPerm && parentPerm.roles) {
        isParentAllowed = parentPerm.roles.some(r => r.toLowerCase() === role);
      }
    }

    if (!isParentAllowed) {
      this.agentOptions = [];
      this.hasAccess = false;
      this.isOpen = false;
      return;
    }

    // 2. Filter allowed agents
    const allowed = isSuperAdmin ? this.rawAgentOptions : this.rawAgentOptions.filter(a => this.isAgentAllowed(a.agentId));
    
    if (allowed.length === 0) {
      this.agentOptions = [];
      this.hasAccess = false;
      this.isOpen = false;
      return;
    }

    this.agentOptions = allowed;
    this.hasAccess = true;

    // Match active agent if already specified or align with default options
    if (this.agentName) {
      const found = this.agentOptions.find(a => a.name.toLowerCase() === this.agentName?.toLowerCase() || a.agentId === this.agentName);
      if (found) {
        this.selectedAgent = found;
        this.activeAgentId = found.agentId;
      } else {
        this.selectedAgent = this.agentOptions[0];
        this.activeAgentId = this.agentOptions[0].agentId;
      }
    } else {
      const found = this.agentOptions.find(a => a.agentId === this.activeAgentId);
      if (found) {
        this.selectedAgent = found;
      } else {
        this.selectedAgent = this.agentOptions[0];
        this.activeAgentId = this.agentOptions[0].agentId;
      }
    }
  }

  private async loadConfiguredAgents() {
    try {
      this.availableAgents = await this.agentProfitService.getActiveAgents();
      
      if (this.availableAgents && this.availableAgents.length > 0) {
        // Dynamically build agentOptions from Firestore agents collection
        const dynamicOptions: AgentOption[] = this.availableAgents.map(ag => {
          const defaultOpt = DEFAULT_AGENT_OPTIONS.find(d => d.agentId === ag.agentId);
          const icon = resolveSmartAgentIcon(ag.agentId, ag.name, ag.icon || defaultOpt?.icon);
          const badge = resolveSmartAgentBadge(ag.agentId, ag.name, ag.badge || defaultOpt?.badge);
          return {
            agentId: ag.agentId,
            name: ag.name,
            icon: icon,
            badge: badge,
            description: ag.description || defaultOpt?.description || `Specialized AI agent for ${ag.name}.`,
            starterPrompts: ag.starterPrompts && ag.starterPrompts.length > 0 ? ag.starterPrompts : (defaultOpt?.starterPrompts || [
              `What can you help me with?`,
              `Show operational overview`,
              `How does this system work?`
            ])
          };
        });

        if (dynamicOptions.length > 0) {
          this.rawAgentOptions = dynamicOptions;
        }
      } else {
        this.rawAgentOptions = DEFAULT_AGENT_OPTIONS;
      }

      this.applyAgentPermissions();
    } catch (e) {
      console.warn('Could not load configured agents from Firestore:', e);
      this.rawAgentOptions = DEFAULT_AGENT_OPTIONS;
      this.applyAgentPermissions();
    }
  }

  get isKioskAgentSelected(): boolean {
    const id = (this.activeAgentId || this.selectedAgent?.agentId || '').toLowerCase().trim();
    return id === 'kiosk-support' || id.includes('kiosk');
  }

  get selectedDeviceDisplayLabel(): string {
    if (!this.selectedDeviceId) return 'None / All Devices';
    const found = this.availableDevices.find(d => d.deviceId === this.selectedDeviceId);
    return found ? `🖥️ ${found.displayLabel}` : `🖥️ ${this.selectedDeviceId}`;
  }

  getFilteredDevices(): DeviceOption[] {
    if (!this.deviceSearchText || !this.deviceSearchText.trim()) {
      return this.availableDevices;
    }
    const kw = this.deviceSearchText.trim().toLowerCase();
    return this.availableDevices.filter(d => 
      d.deviceId.toLowerCase().includes(kw) || 
      (d.deviceName && d.deviceName.toLowerCase().includes(kw)) ||
      (d.storeId && d.storeId.toLowerCase().includes(kw)) ||
      d.displayLabel.toLowerCase().includes(kw)
    );
  }

  getFilteredStarterDevices(): DeviceOption[] {
    if (!this.starterDeviceSearchText || !this.starterDeviceSearchText.trim()) {
      return this.availableDevices;
    }
    const kw = this.starterDeviceSearchText.trim().toLowerCase();
    return this.availableDevices.filter(d => 
      d.deviceId.toLowerCase().includes(kw) || 
      (d.deviceName && d.deviceName.toLowerCase().includes(kw)) ||
      (d.storeId && d.storeId.toLowerCase().includes(kw)) ||
      d.displayLabel.toLowerCase().includes(kw)
    );
  }

  toggleDeviceDropdown(event: Event) {
    event.stopPropagation();
    this.showDeviceDropdownMenu = !this.showDeviceDropdownMenu;
    this.showStarterDeviceDropdownMenu = false;
    if (this.showDeviceDropdownMenu) {
      this.deviceSearchText = '';
    }
  }

  toggleStarterDeviceDropdown(event: Event) {
    event.stopPropagation();
    this.showStarterDeviceDropdownMenu = !this.showStarterDeviceDropdownMenu;
    this.showDeviceDropdownMenu = false;
    if (this.showStarterDeviceDropdownMenu) {
      this.starterDeviceSearchText = '';
    }
  }

  selectDevice(deviceId: string) {
    this.onDeviceSelectChange(deviceId);
    this.showDeviceDropdownMenu = false;
    this.showStarterDeviceDropdownMenu = false;
  }

  private saveCurrentPreferences() {
    this.agentProfitService.saveUserPreferences({
      agentId: this.activeAgentId,
      deviceId: this.selectedDeviceId || ''
    });
  }

  selectAgent(agent: AgentOption) {
    this.selectedAgent = agent;
    this.activeAgentId = agent.agentId;
    this.agentName = agent.name;
    this.showAgentPicker = false;
    if (!this.isKioskAgentSelected) {
      this.selectedDeviceId = '';
    }
    this.saveCurrentPreferences();
    this.setInitialGreeting();
    setTimeout(() => this.chatInput?.nativeElement?.focus(), 100);
  }

  onAgentSelectChange(agentId: string) {
    const found = this.agentOptions.find(a => a.agentId === agentId);
    if (found) {
      this.selectAgent(found);
    }
  }

  toggleAgentPicker() {
    this.showAgentPicker = !this.showAgentPicker;
  }

  onAgentSelect(agentId: string) {
    const found = this.agentOptions.find(a => a.agentId === agentId);
    if (found) {
      this.selectAgent(found);
    } else {
      this.activeAgentId = agentId;
      this.showAgentPicker = false;
      if (!this.isKioskAgentSelected) {
        this.selectedDeviceId = '';
      }
      this.saveCurrentPreferences();
      this.setInitialGreeting();
    }
  }

  private async loadDevices() {
    try {
      const rawDevices = await this.storeService.getAllDevices();
      const mapped: DeviceOption[] = (rawDevices || [])
        .filter(d => d.id !== 'global' && d.deviceId !== 'global')
        .map(d => {
          const id = d.deviceId || d.id;
          const store = d.storeId ? `[${d.storeId}]` : '';
          const name = d.deviceName ? ` — ${d.deviceName}` : '';
          return {
            deviceId: id,
            deviceName: d.deviceName,
            storeId: d.storeId,
            displayLabel: `${id} ${store}${name}`.trim()
          };
        })
        .sort((a, b) => a.deviceId.localeCompare(b.deviceId));
      this.availableDevices = mapped;
    } catch (err) {
      console.warn('Could not load devices for agent chat', err);
    }
  }

  onDeviceSelectChange(deviceId: string) {
    this.selectedDeviceId = deviceId || '';
    this.saveCurrentPreferences();
    if (this.messages.length <= 1) {
      this.setInitialGreeting();
    }
  }

  getFormattedStarterPrompts(): string[] {
    if (!this.isKioskAgentSelected || !this.selectedDeviceId) {
      return this.selectedAgent.starterPrompts;
    }
    return this.selectedAgent.starterPrompts.map(prompt => 
      prompt.replace(/PB-STORE01-01|PB-01/g, this.selectedDeviceId)
    );
  }

  get inputPlaceholder(): string {
    if (this.isKioskAgentSelected && this.selectedDeviceId) {
      return `Ask ${this.selectedAgent.name} about ${this.selectedDeviceId}...`;
    }
    return `Ask ${this.selectedAgent.name}...`;
  }

  private setInitialGreeting() {
    const agent = this.selectedAgent;
    let greeting = `Hi! I am your **${agent.name}** agent.\n\n${agent.description}`;
    if (this.isKioskAgentSelected && this.selectedDeviceId) {
      greeting += `\n\n🎯 **Active Target Device**: \`${this.selectedDeviceId}\` *(Optional hardware context attached)*.`;
    }
    greeting += `\n\nAsk me anything or select one of the suggested prompts below to begin.`;
    this.messages = [{ sender: 'agent', text: greeting, timestamp: Date.now() }];
    const formatted = this.formatMessage(this.messages[0].text);
    this.agentProfitService.clearHistory();
    this.messages[0].html = formatted.safe;
    this.messages[0].rawHtml = formatted.raw;
    this.agentProfitService.saveChatHistory(this.messages);
  }

  sendStarterPrompt(prompt: string) {
    this.userInput = prompt;
    this.showAgentPicker = false;
    this.sendMessage();
  }

  private scrollToBottom() {
    setTimeout(() => {
      const container = this.elementRef.nativeElement.querySelector('.chat-messages');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
  }

  private scrollToMessageStart(index: number) {
    setTimeout(() => {
      const container = this.elementRef.nativeElement.querySelector('.chat-messages');
      const targetMsg = this.elementRef.nativeElement.querySelector(`#msg-${index}`);
      if (container && targetMsg) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetMsg.getBoundingClientRect();
        const targetScrollTop = targetRect.top - containerRect.top + container.scrollTop - 10;
        container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
      }
    }, 60);
  }

  toggleChat() {
    if (!this.hasAccess) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.scrollToBottom();
      setTimeout(() => this.chatInput?.nativeElement?.focus(), 50);
      this.agentProfitService.openChat();
    } else {
      this.agentProfitService.closeChat();
    }
  }

  onResizeStart(event: MouseEvent) {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = this.chatWindow.nativeElement.offsetWidth;
    const startHeight = this.chatWindow.nativeElement.offsetHeight;

    const onMouseMove = (e: MouseEvent) => {
      const newWidth = startWidth - (e.clientX - startX);
      const newHeight = startHeight - (e.clientY - startY);
      
      this.chatWindow.nativeElement.style.width = `${newWidth}px`;
      this.chatWindow.nativeElement.style.height = `${newHeight}px`;
    };

    const onMouseUp = () => {
      this.chatWidth = this.chatWindow.nativeElement.offsetWidth;
      this.chatHeight = this.chatWindow.nativeElement.offsetHeight;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.isOpen && this.isLoading) {
      this.stopThinking();
    }
  }

  stopThinking() {
    if (this.isLoading) {
      this.agentProfitService.cancelAgentRequest();
      this.isLoading = false;
    }
  }

  async clearChat() {
    if (!confirm('Are you sure you want to clear the chat history?')) return;
    this.showAgentPicker = false;
    this.setInitialGreeting();
  }

  async sendMessage() {
    if (!this.userInput.trim()) return;

    const query = this.userInput;
    const formattedUser = this.formatMessage(query);
    this.messages.push({ sender: 'user', text: query, html: formattedUser.safe, rawHtml: formattedUser.raw, timestamp: Date.now() });
    this.scrollToBottom();
    this.agentProfitService.saveChatHistory(this.messages);

    this.userInput = '';
    this.isLoading = true;
    this.scrollToBottom();

    try {
      const context: any = {
        period: this.currentPeriod,
        stats: this.dashboardStats,
        data: this.gridData,
        historicalData: this.historicalData,
        chartOfAccounts: this.chartOfAccounts
      };

      if (this.isKioskAgentSelected && this.selectedDeviceId) {
        context.deviceId = this.selectedDeviceId;
        context.selectedDeviceId = this.selectedDeviceId;
      }

      const response = await this.agentProfitService.askAgent(query, context, this.systemInstruction, this.activeAgentId || 'financial-operations');
      
      const agentMsgIndex = this.messages.length;

      if (response.stopped) {
        this.messages.push({ sender: 'agent', text: response.text, html: this.formatMessage(response.text).safe, rawHtml: response.text, timestamp: Date.now() });
        this.scrollToMessageStart(agentMsgIndex);
        this.agentProfitService.saveChatHistory(this.messages);
        return;
      }

      const formattedAgent = this.formatMessage(response.text);
      this.messages.push({ sender: 'agent', text: response.text, html: formattedAgent.safe, rawHtml: formattedAgent.raw, timestamp: Date.now() });
      this.scrollToMessageStart(agentMsgIndex);
      this.agentProfitService.saveChatHistory(this.messages);
      
      if (response.action) {
        this.agentAction.emit(response);
      }
    } catch (err) {
      const errorMsg = 'Sorry, I encountered an error connecting to the AI.';
      const formattedError = this.formatMessage(errorMsg);
      const errIndex = this.messages.length;
      this.messages.push({ sender: 'agent', text: errorMsg, html: formattedError.safe, rawHtml: formattedError.raw, timestamp: Date.now() });
      this.scrollToMessageStart(errIndex);
      this.agentProfitService.saveChatHistory(this.messages);
    } finally {
      this.isLoading = false;
      setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
    }
  }

  async copyMessage(msg: ChatMessage) {
    try {
      const textBlob = new Blob([msg.text], { type: 'text/plain' });
      
      let htmlContent = msg.rawHtml || msg.text.replace(/\n/g, '<br>');
      htmlContent = htmlContent
        .replace(/<table class="agent-table">/g, '<table style="width: 100%; border-collapse: collapse; margin: 10px 0; font-family: sans-serif; font-size: 14px;">')
        .replace(/<th>/g, '<th style="border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; background-color: #e2e8f0; font-weight: 600; color: #0f172a;">')
        .replace(/<td>/g, '<td style="border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left;">');

      htmlContent = `<div style="font-family: sans-serif; font-size: 14px; line-height: 1.5; color: #0f172a;">${htmlContent}</div>`;

      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      
      const clipboardItem = new ClipboardItem({
        'text/plain': textBlob,
        'text/html': htmlBlob
      });
      
      await navigator.clipboard.write([clipboardItem]);
      
      msg.copied = true;
      setTimeout(() => msg.copied = false, 2000);
    } catch (err) {
      console.error('Failed to copy formatted message:', err);
      navigator.clipboard.writeText(msg.text);
    }
  }

  private formatMessage(text: string): { safe: SafeHtml, raw: string } {
    let html = text || '';
    
    // Parse Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Parse Tables
    const rows = html.split('\n');
    let inTable = false;
    let newHtml = '';
    
    for (let i = 0; i < rows.length; i++) {
      const line = rows[i];
      const trimmed = line.trim();
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const cols = trimmed.split('|').slice(1, -1).map(c => c.trim());
        if (cols.every(c => c.replace(/:/g, '').replace(/-/g, '').length === 0)) {
          continue;
        }
        if (!inTable) {
          inTable = true;
          newHtml += '<table class="agent-table"><thead><tr>';
          cols.forEach(c => newHtml += `<th>${c}</th>`);
          newHtml += '</tr></thead><tbody>';
        } else {
          newHtml += '<tr>';
          cols.forEach(c => newHtml += `<td>${c}</td>`);
          newHtml += '</tr>';
        }
      } else {
        if (inTable) {
          inTable = false;
          newHtml += '</tbody></table><br>';
        }
        newHtml += line + (i === rows.length - 1 ? '' : '<br>');
      }
    }
    if (inTable) {
      newHtml += '</tbody></table>';
    }
    
    return {
      safe: this.sanitizer.bypassSecurityTrustHtml(newHtml.trim()),
      raw: newHtml.trim()
    };
  }
}
